import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useEffect, useState } from 'react';
import PhoneInput from '../../components/inputs/phone';
import { ClearButton, CustomButton } from '../../components/buttons';
import Skeleton from '@mui/material/Skeleton';
import { post } from '../../core/client';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import CreateMvkReservationComponent from './create-mvk-reservation';
import ListMvkReservationComponent from './list-mvk-reservation-info';

interface MvkReservationInfoRequest {
    phone: string;
}

interface MvkReservationInfoResponse {
    memberId: number;
    firstName: string;
    lastName: string;
    company: string;
    lastReservationDate?: string;
    isReservationAllowed: boolean;
}

const initialReqState: MvkReservationInfoRequest = {
    phone: ""
}

const initialResState: MvkReservationInfoResponse = {
    memberId: 0,
    firstName: "",
    lastName: "",
    company: "",
    lastReservationDate: undefined,
    isReservationAllowed: false
}

const MvkReservationInfoComponent = () => {

    const [requestState, setRequestState] = useState<MvkReservationInfoRequest>(initialReqState);
    const [responseState, setResponseState] = useState<MvkReservationInfoResponse>(initialResState);
    const [loading, setLoading] = useState(false);
    const [errorText, setErrorText] = useState<string>('');

    const [disabledMonths, setDisabledMonths] = useState<Set<string>>();
    const [isRefreshRequired, setIsRefreshRequired] = useState(false);

    const handleRequestChange = (e: any) => {
        setRequestState(prevState => ({
            ...prevState,
            [e.target.name]: e.target.value,
        }));
    };

    const handleResetForm = () => {
        setRequestState(initialReqState);
        setResponseState(initialResState);
        setErrorText('');
    };

    useEffect(() => {
        (async () => {
            if (isRefreshRequired) {
                await handleGetMemberReservationInfo();
                setIsRefreshRequired(false);
            }
        })();
    }, [isRefreshRequired]);

    const handleGetMemberReservationInfo = async () => {
        setLoading(true);
        const response = await post<MvkReservationInfoResponse>('api/external/get-mvk-reservation-info', requestState);
        if (response) {
            setErrorText("");
            setResponseState(response);
        } else {
            setErrorText("Sorgulama işlemi tamamlanamadı");
        }
        setLoading(false);
    }

    const renderContent = () => {
        if (loading) {
            return (
                <Box pt={1} width="90%">
                    <Grid container xl spacing={3}>
                        <Grid item md={7}>
                            <Skeleton animation="wave" height={60} />
                            <Skeleton animation="wave" height={60} />
                            <Skeleton animation="wave" height={60} />
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item md={5}>
                            <Skeleton animation="wave" height="100%" />
                        </Grid>
                    </Grid>
                </Box>
            );
        }

        if (errorText !== '') {
            return <Typography variant="subtitle1" display="block">{errorText}</Typography>;
        }
        if (!loading && errorText === '' && responseState.memberId !== 0) {
            return (
                <Box p={2} pt={3} width="100%">
                    <Grid container xl spacing={3}>
                        <Grid item md={7}>
                            <div className="widget-area" style={{ height: '100%' }}>
                                {renderResultContent()}
                            </div>
                        </Grid>
                        <Grid item md={5}>
                            <div className="widget-area" style={{ height: '100%' }}>
                                <CreateMvkReservationComponent memberId={responseState.memberId} disabledMonths={disabledMonths} onCreated={setIsRefreshRequired} />
                            </div>
                        </Grid>
                        <Grid item md={12}>
                            <div className="widget-area" style={{ height: '100%' }}>
                                <ListMvkReservationComponent memberId={responseState.memberId} onDisabledMonthsChange={setDisabledMonths} isReport={false} onCancel={setIsRefreshRequired} />
                            </div>
                        </Grid>
                    </Grid>
                </Box>
            );
        }

        return <div></div>;
    };

    const renderResultContent = () => {
        return (
        <Card className={'member-card'} variant="outlined">
            <CardContent>
                <Typography gutterBottom component="h3">
                    Üye Bilgileri
                </Typography>
                <Divider />
                <Typography variant="body2" color="textSecondary" component="div">
                    {
                        responseState.memberId !== 0 ?
                            <Box py={1}>
                                <table className="member-info-table">
                                    <tbody>
                                        <tr>
                                            <td>Adı:</td>
                                            <td>
                                                <strong>{responseState.firstName} {responseState.lastName}</strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Çalıştığı Şirket:</td>
                                            <td>
                                                <strong>{responseState.company}</strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Son Rezervasyon Tarihi:</td>
                                            <td>
                                                <strong>{responseState.lastReservationDate === undefined ? "Geçmiş rezervasyon bulunmuyor" : responseState.lastReservationDate}</strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Rezervasyon Hakkı:</td>
                                            <td>
                                                <strong>{responseState.isReservationAllowed ? "Var" : "Yok"}</strong>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </Box>
                            : null
                    }
                </Typography>
            </CardContent>
        </Card>
        );
    };

    return (
        <>
            <div className="page-header">
                <div className="page-title">
                    <h2>MVK Rezervasyon Sorgulama</h2>
                </div>
            </div>

            <div className="page-content">
                <Grid container direction="column" alignItems="center">
                    <Box className="member-search" p={2}>
                        <Box className="form-field" mb={2}>
                            <PhoneInput
                                id="phone"
                                name="phone"
                                caption="Üye telefon numarasını giriniz"
                                onChange={handleRequestChange}
                                value={requestState.phone}
                            />
                        </Box>
                        <Grid container spacing={1} justifyContent="center">
                            <Grid item>
                                <CustomButton
                                    text="ÜYE SORGULA"
                                    className='ks-btn btn-primary'
                                    disabled={loading}
                                    loading={loading}
                                    onClick={async () => await handleGetMemberReservationInfo()}
                                />
                            </Grid>
                            <Grid item>
                                <ClearButton
                                    disabled={loading || requestState.phone === ''}
                                    onClick={() => handleResetForm()}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    {renderContent()}
                </Grid>
            </div>
        </>
    )

};

export default MvkReservationInfoComponent;